import { Matrix23 } from '@seddi/math-v2';
import {
  type BufferAttribute,
  type BufferGeometry,
  Box3,
  Sphere,
  Vector2,
  Vector3,
} from '@seddi/three';

import { getInverseTransformMatrix } from '../core/transform2';
import type { StyleLogoRegion } from '../api/styleAggregator';
import type { FrameBounds } from './frameLookAt';
import { boxIntersectsPoint, getBoxLocal } from './frameStyleRegion';

const boxLocal = { min: { x: 0, y: 0 }, max: { x: 0, y: 0 } };
const worldToLocal = new Matrix23();
const v2 = new Vector2();

export const computeFrameBounds = (
  region: StyleLogoRegion,
  geometry: BufferGeometry,
): FrameBounds => {
  getBoxLocal(region, boxLocal);
  getInverseTransformMatrix(
    region.transform as { translation: { x: number; y: number }; rotation: number },
    worldToLocal,
  );

  const positionAttr = geometry.getAttribute('position') as BufferAttribute;
  const normalAttr = geometry.getAttribute('normal') as BufferAttribute;
  const uvAttr = geometry.getAttribute('uv') as BufferAttribute;

  if (!positionAttr || !normalAttr || !uvAttr) {
    return {
      normal: new Vector3(0, 0, 1),
      sphere: new Sphere(new Vector3(0, 0, 0), 0.5),
    };
  }

  const boxWorld = new Box3();
  const normal = new Vector3();
  const v3 = new Vector3();

  for (let i = 0, n = positionAttr.count; i < n; i++) {
    v2.set(uvAttr.getX(i), uvAttr.getY(i));
    worldToLocal.transformRef(v2);
    if (boxIntersectsPoint(boxLocal, v2.x, v2.y)) {
      v3.set(positionAttr.getX(i), positionAttr.getY(i), positionAttr.getZ(i));
      boxWorld.expandByPoint(v3);
      v3.set(normalAttr.getX(i), normalAttr.getY(i), normalAttr.getZ(i));
      normal.add(v3);
    }
  }

  const sphere = new Sphere();
  boxWorld.getBoundingSphere(sphere);

  if (normal.lengthSq() === 0) {
    if (geometry.boundingSphere === null) {
      geometry.computeBoundingSphere();
    }
    normal.set(0, 0, 1);
    if (geometry.boundingSphere) {
      sphere.copy(geometry.boundingSphere);
    }
  } else {
    normal.normalize();
  }

  return { normal, sphere };
};
